/* @flow */

import type { Product as ProductType, ProductCardProduct, ProductCategory } from "shop-state/types";
import type { Option } from "@crossroads/ui-components";

import React, { useState, useEffect, useMemo } from "react";
import { useAnalytics } from "context/analytics";
import { useData } from "crustate/react";
import { useLocation } from "react-router";
import { Helmet } from "react-helmet-async";
import { useTranslate } from "@awardit/react-use-translate";
import { useGetProductMeta } from "helpers/get-meta";
import { QuoteData, CmsBlockData } from "state/data";
import useFormat from "helpers/hooks/use-format";
import { Link } from "react-router-dom";
import ProductList from "components/ProductList";
import CategorySection from "components/CategorySection";
import Breadcrumbs from "components/Breadcrumbs";
import useShowPrices from "helpers/use-show-prices";
import useBrowserDimensions from "helpers/hooks/use-browser-dimensions";
import {
  Accordion,
  ProductViewMedia,
  Button,
  AutoExpand,
  AddToCart,
  productDefaults,
  inStock,
  getAttributesConfigurable,
  getSelectedConfigurable,
  Wrapper,
  getPrice,
  PriceLockPrice,
  PriceLockLoading,
} from "@crossroads/ui-components";
import { ColorSelect } from "components/ProductOptions";
import { pointsPriceByID } from "helpers/points";
import useBreadcrumbLinks from "helpers/hooks/use-breadcrumb-links";
import CloseIcon from "icons/close.svg";
import ChevronIcon from "icons/chevron-big.svg";

import styles from "./styles.scss";

type ProductProps = {
  product: ProductType,
};

type HintProductProps = {
  product: ProductCardProduct,
};

type StructuredCategory = {
  name: string,
  url: string,
  children: Array<{
    name: string,
    url: string,
  }>,
};

type ProductsPerRow = 2 | 3 | 4;

const CustomCloseIcon = (): React$Node => {
  const t = useTranslate();

  return <CloseIcon aria-label={t("ICONS.CROSS")} className={styles.customCloseIcon} />;
};

const AddToCartBtn = ({
  loading = false, outOfStock,
}: { loading?: boolean, outOfStock: boolean,
}): React$Node => {
  const t = useTranslate();
  const quoteState = useData(QuoteData);
  const addingToCart = quoteState.state === "ADDING_ITEM";

  return (
    <Button
      className={styles.addToCartButton}
      variant="primary"
      loading={addingToCart || loading}
      disabled={outOfStock}
      type="submit"
    >
      {outOfStock ? t("PRODUCT.OUT_OF_STOCK") : t("PRODUCT.ADD_TO_CART")}
    </Button>
  );
};

const DiscountSection = ({
  discount,
}: { discount: number,
}): React$Node => {
  const t = useTranslate();

  if (discount > 0 && discount < 100) {
    return (
      <div className={styles.discount}>
        <span>{t("PRODUCT.DISCOUNT", { discount })}</span>
      </div>
    );
  }

  return null;
};

const mapProductCategories = (
  categories: $ReadOnlyArray<ProductCategory>
): Array<StructuredCategory> => {
  const structuredCategories: Array<StructuredCategory> = [];

  for (const category of categories) {
    if (category.parent) {
      let parent = structuredCategories.find(c => c.url === category.parent?.url);

      if (parent) {
        parent.children.push({
          name: category.name,
          url: category.url,
        });
      }
      else {
        parent = {
          name: category.parent?.name || "",
          url: category.parent?.url || "",
          children: [{
            name: category.name,
            url: category.url,
          }],
        };

        structuredCategories.push(parent);
      }
    }
    else {
      structuredCategories.push({
        name: category.name,
        url: category.url,
        children: [],
      });
    }
  }

  return structuredCategories;
};

const RelatedProductsSection = ({
  product,
  productsPerRow,
  t,
}: {
  product: ProductType,
  productsPerRow: () => ProductsPerRow,
  t: (string, args?: {}) => string,
}): React$Node => {
  const relatedProducts = product.relatedProducts.items;
  const crossSellProducts = product.crossSellProducts.items;
  const productCount = productsPerRow();

  if (relatedProducts.length === 0 && crossSellProducts.length === 0) {
    return null;
  }

  return (
    <AutoExpand>
      &nbsp;
      <div className={styles.lists}>
        {relatedProducts.length > 0 &&
          <div className={styles.relatedList}>
            <ProductList
              heading={t("PRODUCT.RELATED_PRODUCTS")}
              products={relatedProducts.slice(0, productCount)}
              productsPerRow={productCount}
            />
          </div>
        }

        {crossSellProducts.length > 0 &&
          <div className={styles.historyList}>
            <ProductList
              heading={t("PRODUCT.CROSS_SELL_PRODUCTS")}
              products={crossSellProducts.slice(0, productCount)}
              productsPerRow={productCount}
            />
          </div>
        }
      </div>
    </AutoExpand>
  );
};

const Product = ({ product }: ProductProps): React$Node => {
  const t = useTranslate();
  const location = useLocation();
  const analytics = useAnalytics();
  const campaignData = useData(CmsBlockData);
  const { showPrices, showPricesRead } = useShowPrices();
  const { formatPrice } = useFormat();
  const configAttributes = product.type === "configurable" ? getAttributesConfigurable(product) : {};
  const [selected, setSelected] = useState<Option>(productDefaults(product));
  const selectedItem = getSelectedConfigurable(selected, configAttributes);
  const { price } = (selectedItem ?? product);
  const { msrp } = (selectedItem ? selectedItem.product.attributes : product.attributes);
  const brand = product.attributes.manufacturer;
  const { manufacturer } = product.attrDescriptions;
  const meta = useGetProductMeta(product, t);
  const outOfStock = !inStock(product, selected);
  const { smallImage, largeImage } = product.attributes;
  const pointsPrice = pointsPriceByID(product.pointsPrices, "vanilla");
  const { attributes: { showDiscount } } = product;
  const { width: browserWidth } = useBrowserDimensions();
  const categories = useMemo(() => mapProductCategories(product.categories), [product]);
  const breadcrumbLinks = useBreadcrumbLinks({ fallbackCategories: categories });
  const discount = (showDiscount === true && typeof msrp === "number") ?
    Math.floor((msrp - price.incVat) / msrp * 100) : 0;

  const [currentImage, setCurrentImage] = useState({
    smallImage: smallImage ? { x1: smallImage.x1, x2: smallImage.x2 } : null,
    largeImage: largeImage ? { x1: largeImage.x1, x2: largeImage.x2 } : null,
  });

  const numberOfProductsToShow = (): ProductsPerRow => {
    if (browserWidth < Number.parseInt(styles.tiny, 10)) {
      return 2;
    }

    if (browserWidth < Number.parseInt(styles.medium, 10)) {
      return 3;
    }

    return 4;
  };

  useEffect(() => {
    analytics.registerProductDetailsView({
      sku: product.sku,
      name: product.name,
      price: product.price,
      qty: 1,
      attributes: {
        manufacturer: product.attributes.manufacturer,
      },
      categories: product.categories,
    },
    product.price.incVat,
    location.state?.list,
    location.state?.position);
  }, []);

  // Change image when selecting option
  useEffect(() => {
    if (selectedItem) {
      const a = selectedItem.product.attributes;

      if (a) {
        setCurrentImage({
          smallImage: a.smallImage ? { x1: a.smallImage.x1, x2: a.smallImage.x2 } : null,
          largeImage: a.largeImage ? { x1: a.largeImage.x1, x2: a.largeImage.x2 } : null,
        });
      }
    }
  }, [selected]);

  useEffect(() => {
    setCurrentImage({ smallImage, largeImage });
  }, [smallImage, largeImage]);

  const onAdd = p => {
    if (selectedItem) {
      const incVat = getPrice(product, selected, "incVat");
      const exVat = getPrice(product, selected, "exVat");

      analytics.registerModifyCart({
        ...selectedItem.product,
        sku: product.sku,
        qty: 1,
        price: { incVat, exVat, vat: incVat - exVat },
        categories: p.categories,
      }, 1, incVat);
    }
    else {
      analytics.registerModifyCart({
        ...p,
        qty: 1,
      }, 1, p.price.incVat);
    }
  };

  return (
    <Wrapper>
      <Helmet
        title={meta.title}
        meta={meta.data}
        link={meta.link}
      />

      <Breadcrumbs
        className={styles.breadcrumbs}
        links={breadcrumbLinks}
        current={product.name}
      />

      <div className={styles.split}>
        <div className={styles.left}>
          <ProductViewMedia
            CloseIcon={CustomCloseIcon}
            alt={product.name}
            setCurrentImage={setCurrentImage}
            currentImage={currentImage}
            gallery={product.gallery}
            location={location}
            galleryLocation="bottom"
          />
          <DiscountSection discount={discount} />
        </div>
        <div className={styles.right}>
          <header className={styles.header__inner}>
            <div className={styles.top}>
              <h1 className={styles.name}>{product.name}</h1>
              <Link
                to={{
                  pathname: `/brand/${encodeURIComponent(brand)}`,
                  state: { hint: { category: { name: brand } } },
                }}
                className={styles.brand}
              >
                {brand}
              </Link>
            </div>
          </header>
          {showPricesRead && (
            <PriceLockPrice
              className={styles.price}
              price={{ ...price, exVat: getPrice(product, selected, "exVat") }}
              pointsPrice={pointsPrice}
              msrp={msrp}
              showPrice={showPrices}
              showDiscount={false}
              formatPrice={formatPrice}
              view="PRODUCT_VIEW"
              externalLink={t("COMMON.EXTERNAL.URL")}
              btnVariantProductView="primary"
            />
          )}
          {!showPricesRead && <PriceLockLoading />}
          {showPrices && (
            <section className={styles.addToCart}>
              <AddToCart
                product={product}
                selected={selected}
                setSelected={setSelected}
                qty={1}
                templates={{
                  configurable: ColorSelect,
                }}
                onAdd={onAdd}
              >
                <AddToCartBtn outOfStock={outOfStock} />
              </AddToCart>
            </section>
          )}

          <section className={styles.productSection}>
            <Accordion
              id="productDescription"
              title={t("PRODUCT.DESCRIPTION")}
              ToggleIcon={ChevronIcon}
            >
              <div
                dangerouslySetInnerHTML={{ __html: product.attributes.description }}
                className={styles.description}
              />
            </Accordion>

            {manufacturer && manufacturer.icon && manufacturer.description &&
              <Accordion
                id="aboutBrand"
                title={t("PRODUCT.ABOUT_BRAND", { brand: manufacturer.title })}
                ToggleIcon={ChevronIcon}
              >
                <div className={styles.manufacturer}>
                  <Link
                    to={{
                      pathname: `/brand/${encodeURIComponent(brand)}`,
                      state: { hint: { category: { name: brand } } },
                    }}
                    className={styles.logo}
                  >
                    <img
                      src={manufacturer ? manufacturer.icon : ""}
                      alt={`Logo ${brand}`}
                    />
                  </Link>
                  <p>{manufacturer ? manufacturer.description : ""}</p>
                  <Link
                    to={{
                      pathname: `/brand/${encodeURIComponent(brand)}`,
                      state: { hint: { category: { name: brand } } },
                    }}
                    className={styles.goto}
                  >
                    {t("PRODUCT.GO_TO_MANUFACTURER", { brand: manufacturer.title })}
                  </Link>
                </div>
              </Accordion>
            }
          </section>
        </div>
      </div>
      <RelatedProductsSection
        product={product}
        productsPerRow={numberOfProductsToShow}
        t={t}
      />
      <CategorySection useWrapper />
      {campaignData.state === "LOADED" && campaignData.data !== null &&
        <div
          dangerouslySetInnerHTML={{ __html: campaignData.data.content }}
          className={styles.productCampaignCard}
        />
      }
    </Wrapper>
  );
};

export const HintProduct = ({ product }: HintProductProps): React$Node => {
  const location = useLocation();
  const brand = product.attributes.manufacturer;
  const image = product.attributes.largeImage ||
  product.attributes.smallImage ||
  location.state?.image || { x1: "", x2: "" };
  const breadcrumbLinks = useBreadcrumbLinks();

  return (
    <Wrapper className={styles.hintWrapper}>
      <Breadcrumbs
        className={styles.breadcrumbs}
        links={breadcrumbLinks}
        current={product.name}
      />

      <div className={styles.split}>
        <div className={styles.left}>
          <ProductViewMedia
            alt={product.name}
            setCurrentImage={() => {}}
            currentImage={{
              largeImage: image,
              smallImage: image,
            }}
            gallery={[]}
            location={location}
            galleryLocation="bottom"
          />
        </div>
        <div className={styles.right}>
          <header className={styles.header__inner}>
            <div className={styles.top}>
              <h1 className={styles.name}>{product.name}</h1>
              <Link
                to={{
                  pathname: `/brand/${encodeURIComponent(brand)}`,
                  state: { hint: { category: { name: brand } } },
                }}
                className={styles.brand}
              >
                {brand}
              </Link>
            </div>
          </header>

          <div className={styles.dummyContent}>
            <div className={styles.top} />
            <div className={styles.middle} />
            <div className={styles.bottom} />
          </div>
        </div>
      </div>
    </Wrapper>
  );
};

export default Product;
